import { useState, useCallback } from 'react';
import { uploadAsset } from '../api/aws/aws';
import { uploadPdfApi } from '../api/elevate/uploadPdf';
import { toast } from 'react-hot-toast';

export interface Resource {
  originalName: string;
  fileKey: string;
  category: string;
  uploadedAt: Date;
}

export interface UseResourceUploadProps {
  categories: string[];
  existingResources?: Resource[];
}

export interface UseResourceUploadReturn {
  resources: Record<string, Resource[]>;
  newlyUploaded: Record<string, Resource[]>;
  isUploading: Record<string, boolean>;
  handleNewFiles: (files: File[], category: string) => Promise<void>;
  removeResource: (index: number, category: string) => void;
  handleViewResource: (resource: Resource) => Promise<void>;
  uploadToServer: (brandId: string, brandResources: Resource[]) => Promise<void>;
  isUploadingToServer: boolean;
}

export const useResourceUpload = ({
  categories,
  existingResources = [],
}: UseResourceUploadProps): UseResourceUploadReturn => {
  // Initialize state for each category
  const [resources, setResources] = useState<Record<string, Resource[]>>(() => {
    const initial: Record<string, Resource[]> = {};
    categories.forEach(category => {
      initial[category] = existingResources.filter(r => r.category === category);
    });
    return initial;
  });

  const [newlyUploaded, setNewlyUploaded] = useState<Record<string, Resource[]>>(() => {
    const initial: Record<string, Resource[]> = {};
    categories.forEach(category => {
      initial[category] = [];
    });
    return initial;
  });

  const [isUploading, setIsUploading] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    categories.forEach(category => {
      initial[category] = false;
    });
    return initial;
  });

  const [isUploadingToServer, setIsUploadingToServer] = useState(false);

  const handleNewFiles = useCallback(
    async (files: File[], category: string) => {
      setIsUploading(prev => ({ ...prev, [category]: true }));

      try {
        const uploaded = await Promise.all(
          files.map(async (file) => ({
            originalName: file.name,
            fileKey: await uploadAsset(file, 'APPLICANTS', 'resources'),
            category,
            uploadedAt: new Date(),
          }))
        );

        setResources(prev => ({
          ...prev,
          [category]: [...prev[category], ...uploaded]
        }));

        setNewlyUploaded(prev => ({
          ...prev,
          [category]: [...prev[category], ...uploaded]
        }));

        toast.success('Files uploaded successfully!');
      } catch (err) {
        toast.error('Failed to upload files.');
        console.error('Upload error:', err);
      } finally {
        setIsUploading(prev => ({ ...prev, [category]: false }));
      }
    },
    []
  );

  const removeResource = useCallback(
    (index: number, category: string) => {
      const removed = resources[category][index];
      
      setResources(prev => ({
        ...prev,
        [category]: prev[category].filter((_, i) => i !== index)
      }));

      setNewlyUploaded(prev => ({
        ...prev,
        [category]: prev[category].filter(f => f.fileKey !== removed?.fileKey)
      }));
    },
    [resources]
  );

  const handleViewResource = useCallback(async (resource: Resource) => {
    if (!resource.fileKey) return;

    try {
      const { getAssets } = await import('../api/queries');
      const data = await getAssets(resource.fileKey);
      if (data?.success && data?.asset?.[0]?.url) {
        window.open(data.asset[0].url, '_blank');
      } else {
        toast.error('Error fetching asset.');
      }
    } catch {
      toast.error('Failed to fetch signed URL');
    }
  }, []);

  const uploadToServer = useCallback(async (brandId: string, brandResources: Resource[]) => {
    const allUploads = Object.values(newlyUploaded).flat();
    if (!allUploads.length) return;

    setIsUploadingToServer(true);

    try {
      const toPdfPayload = (category: string) =>
        allUploads
          .filter(file => file.category === category)
          .map(file => {
            // Use the fileKey from brand resources returned by the API
            const resource = brandResources.find(
              (r: Resource) => r.originalName === file.originalName
            );
            if (!resource?.fileKey) return null;
            return {
              s3_url: resource.fileKey,
              filename: file.originalName,
              pdf_type: file.category,
            };
          })
          .filter(Boolean);

      const payload: Record<string, any> = {};
      categories.forEach(category => {
        payload[category] = toPdfPayload(category);
      });

      if (Object.values(payload).some(arr => (arr as any[]).length > 0)) {
        await uploadPdfApi(brandId, payload as any);
      }
    } catch (err) {
      console.error('PDF upload failed:', err);
      throw err;
    } finally {
      setIsUploadingToServer(false);
    }
  }, [newlyUploaded, categories]);

  return {
    resources,
    newlyUploaded,
    isUploading,
    handleNewFiles,
    removeResource,
    handleViewResource,
    uploadToServer,
    isUploadingToServer,
  };
};